import axios from 'axios';
import { AuthResponse, ApiResponse, Service, Booking, User, Provider, Schedule, Payment, Category, AdminSettings } from '../types';

// Lazy load Sentry to make it optional
const getSentry = () => {
  try {
    return require('@sentry/react');
  } catch (e) {
    return null;
  }
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
console.log('API_BASE_URL:', API_BASE_URL);
console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and capture errors to Sentry
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    // Capture 500+ errors to Sentry if available
    const Sentry = getSentry();
    if (Sentry && error.response?.status >= 500) {
      Sentry.captureException(error, {
        tags: {
          api_error: true,
          status_code: error.response?.status,
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          message: error.response?.data?.message,
        },
      });
    }
    
    // Capture client errors (400-499, except 401)
    if (Sentry && error.response?.status >= 400 && error.response?.status < 500 && error.response?.status !== 401) {
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          api_error: true,
          status_code: error.response?.status,
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          message: error.response?.data?.message,
        },
      });
    }
    
    // Capture network errors
    if (Sentry && !error.response && error.request) {
      Sentry.captureException(error, {
        tags: {
          network_error: true,
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
        },
      });
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData: {
    name: string;
    email: string;
    password: string;
    phone: string;
    role?: string;
  }): Promise<AuthResponse> => api.post('/api/auth/register', userData).then(res => res.data),

  login: (credentials: {
    email: string;
    password: string;
  }): Promise<AuthResponse> => api.post('/api/auth/login', credentials).then(res => res.data),

  getProfile: (): Promise<ApiResponse<{ user: User }>> => api.get('/api/auth/profile').then(res => res.data),

  completeProfile: (profileData: {
    phone: string;
    role?: string;
    skills?: string[];
    policeVerificationDocument?: string;
  }): Promise<AuthResponse> => api.post('/api/auth/complete-profile', profileData).then(res => res.data),

  updateLocation: (locationData: {
    latitude: number;
    longitude: number;
    address?: string;
  }): Promise<AuthResponse> => api.put('/api/auth/location', locationData).then(res => res.data),

  updateProfileData: (profileData: any): Promise<AuthResponse> => 
    api.put('/api/auth/profile', profileData).then(res => res.data),

  requestAccountDeletion: (reason?: string): Promise<ApiResponse<{
    status: string;
    deletedAt: string;
    deletionDate: string;
    gracePeriodDays: number;
    canCancel: boolean;
  }>> => api.post('/api/auth/request-account-deletion', { reason }).then(res => res.data),

  cancelAccountDeletion: (): Promise<ApiResponse<{
    status: string;
    deletedAt: null;
  }>> => api.post('/api/auth/cancel-account-deletion').then(res => res.data),

  checkOTPEnabled: (): Promise<ApiResponse<{ enabled: boolean; featureName: string }>> =>
    api.get('/api/auth/otp-enabled').then(res => res.data),

  sendOTP: (data: {
    phone: string;
    email?: string;
  }): Promise<ApiResponse<{ phone: string; otp?: string }>> => 
    api.post('/api/auth/send-otp', data).then(res => res.data),

  verifyOTP: (data: {
    phone: string;
    otp: string;
    email?: string;
  }): Promise<AuthResponse> => 
    api.post('/api/auth/verify-otp', data).then(res => res.data),
};

// Services API
export const servicesAPI = {
  getServices: (params?: {
    category?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ services: Service[]; pagination: any }>> => 
    api.get('/api/services', { params }).then(res => res.data),

  getServiceById: (id: string): Promise<ApiResponse<{ service: Service }>> => 
    api.get(`/api/services/${id}`).then(res => res.data),

  createService: (serviceData: {
    name: string;
    description: string;
    category: string;
    price: number;
    duration: number;
  }): Promise<ApiResponse<{ service: Service }>> => 
    api.post('/api/services', serviceData).then(res => res.data),

  updateService: (id: string, serviceData: Partial<Service>): Promise<ApiResponse<{ service: Service }>> => 
    api.put(`/api/services/${id}`, serviceData).then(res => res.data),

  deleteService: (id: string): Promise<ApiResponse<null>> => 
    api.delete(`/api/services/${id}`).then(res => res.data),

  getMyServices: (): Promise<ApiResponse<{ services: Service[] }>> => 
    api.get('/api/services/my/services').then(res => res.data),
};

// Providers API
export const providersAPI = {
  getProviders: (params?: {
    category?: string;
    skill?: string;
    latitude?: number;
    longitude?: number;
  }): Promise<ApiResponse<{ providers: Provider[] }>> => 
    api.get('/api/providers', { params }).then(res => res.data),

  getProviderById: (id: string): Promise<ApiResponse<{ provider: Provider }>> => 
    api.get(`/api/providers/${id}`).then(res => res.data),
  
  getProvidersByCategory: (categorySlug: string, params?: {
    latitude?: number;
    longitude?: number;
    rating?: number;
    experience?: number;
  }): Promise<ApiResponse<Provider[]>> => 
    api.get(`/api/categories/${categorySlug}/providers`, { params }).then(res => res.data),
};

// Schedules API
export const schedulesAPI = {
  getProviderSchedules: (providerId: string, params?: {
    date?: string;
  }): Promise<ApiResponse<{ schedules: Schedule[] }>> => 
    api.get(`/api/schedules/provider/${providerId}`, { params }).then(res => res.data),

  getMySchedules: (): Promise<ApiResponse<{ schedules: Schedule[] }>> => 
    api.get('/api/schedules/my-schedules').then(res => res.data),

  createOrUpdateSchedule: (scheduleData: {
    date: string;
    timeSlots: Array<{
      startTime: string;
      endTime: string;
      isBooked?: boolean;
    }>;
    isAvailable: boolean;
  }): Promise<ApiResponse<{ schedule: Schedule }>> => 
    api.post('/api/schedules', scheduleData).then(res => res.data),

  toggleScheduleAvailability: (scheduleId: string): Promise<ApiResponse<{ schedule: Schedule }>> => 
    api.patch(`/api/schedules/${scheduleId}/toggle-availability`).then(res => res.data),

  deleteSchedule: (scheduleId: string): Promise<ApiResponse<null>> => 
    api.delete(`/api/schedules/${scheduleId}`).then(res => res.data),

  getDriverBookedTimes: (providerId: string, date: string): Promise<ApiResponse<{
    date: string;
    hasSchedule: boolean;
    isAvailable: boolean;
    bookedTimes: Array<{ startTime: string; endTime: string }>;
    availableSlots: Array<{ startTime: string; endTime: string }>;
    totalSlots: number;
    driver: { id: string; name: string; avatar?: string };
  }>> => 
    api.get(`/api/schedules/driver/${providerId}/booked-times/${date}`).then(res => res.data),
};

// Bookings API
export const bookingsAPI = {
  createBooking: (bookingData: {
    providerId: string;
    bookingDate: string;
    timeSlot: string;
    notes?: string;
    serviceType?: string;
    pickupLocation?: {
      latitude: number;
      longitude: number;
      address?: string;
    };
    dropoffLocation?: {
      latitude: number;
      longitude: number;
      address?: string;
    };
    numberOfNights?: number;
    intermediateDestinations?: Array<{
      latitude: number;
      longitude: number;
      address: string;
      order: number;
    }>;
    tripType?: 'roundTrip' | 'oneWay';
    tripStartTime?: string;
    tripEndTime?: string;
  }): Promise<ApiResponse<{ booking: Booking; duration: number; hourlyRate: number }>> => 
    api.post('/api/bookings', bookingData).then(res => res.data),

  getMyBookings: (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Booking[]>> => 
    api.get('/api/bookings/my-bookings', { params }).then(res => res.data),

  getProviderBookings: (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Booking[]>> => 
    api.get('/api/bookings/provider-bookings', { params }).then(res => res.data),

  updateBookingStatus: (id: string, status: string, cancellationReason?: string): Promise<ApiResponse<{ booking: Booking }>> =>
    api.patch(`/api/bookings/${id}/status`, { status, cancellationReason }).then(res => res.data),
  
  getCancellationReasons: (): Promise<ApiResponse<{ reasons: string[] }>> =>
    api.get('/api/cancellation-reasons').then(res => res.data),

  getBookingById: (id: string): Promise<ApiResponse<{ booking: Booking }>> => 
    api.get(`/api/bookings/${id}`).then(res => res.data),

  getBookingStats: (): Promise<ApiResponse<any>> => 
    api.get('/api/bookings/stats').then(res => res.data),

  // Driver request APIs
  createDriverRequest: (requestData: {
    tripStartDateTime: string; // Scheduled trip start datetime (ISO string) - REQUIRED
    tripEndDateTime: string; // Scheduled trip end datetime (ISO string) - REQUIRED
    notes?: string;
    pickupLocation: {
      latitude: number;
      longitude: number;
      address?: string;
    };
    dropoffLocation: {
      latitude: number;
      longitude: number;
      address?: string;
    };
    numberOfNights?: number;
    intermediateDestinations?: Array<{
      latitude: number;
      longitude: number;
      address: string;
      order: number;
    }>;
  }): Promise<ApiResponse<{ requestId: string; expiresAt: string; timeoutMinutes: number }>> => 
    api.post('/api/bookings/driver-request', requestData).then(res => res.data),

  getDriverRequestStatus: (requestId: string): Promise<ApiResponse<{
    status: 'pending' | 'accepted' | 'expired' | 'cancelled';
    expiresAt?: string;
    booking?: Booking;
    cancellationReason?: string;
  }>> => 
    api.get(`/api/bookings/driver-request/${requestId}/status`).then(res => res.data),

  acceptDriverRequest: (requestId: string): Promise<ApiResponse<{ booking: Booking }>> => 
    api.post(`/api/bookings/driver-request/${requestId}/accept`).then(res => res.data),

  ignoreDriverRequest: (requestId: string): Promise<ApiResponse<any>> => 
    api.post(`/api/bookings/driver-request/${requestId}/ignore`).then(res => res.data),

  getDriverRequests: (): Promise<ApiResponse<any[]>> => 
    api.get('/api/bookings/driver-requests').then(res => res.data),

  cancelDriverRequest: (requestId: string, cancellationReason: string): Promise<ApiResponse<any>> =>
    api.patch(`/api/bookings/driver-request/${requestId}/cancel`, { cancellationReason }).then(res => res.data),

  adminAssignDriverRequest: (requestId: string, driverId: string, additionalData?: any): Promise<ApiResponse<{ booking: Booking }>> =>
    api.post(`/api/bookings/driver-request/${requestId}/admin-assign`, { driverId, ...additionalData }).then(res => res.data),
  
  // OTP verification for trip start
  verifyTripOTP: (bookingId: string, otp: string): Promise<ApiResponse<{ booking: Booking }>> =>
    api.post(`/api/bookings/${bookingId}/verify-otp`, { otp }).then(res => res.data),
  
  // Update driver location
  updateDriverLocation: (bookingId: string, latitude: number, longitude: number): Promise<ApiResponse<any>> =>
    api.post(`/api/bookings/${bookingId}/update-location`, { latitude, longitude }).then(res => res.data),
  
  // Send booking reminder
  sendBookingReminder: (bookingId: string): Promise<ApiResponse<any>> =>
    api.post(`/api/bookings/${bookingId}/send-reminder`).then(res => res.data),
  
  // Rate driver
  rateDriver: (bookingId: string, rating: number, comment?: string): Promise<ApiResponse<{ booking: Booking }>> =>
    api.post(`/api/bookings/${bookingId}/rate-driver`, { rating, comment }).then(res => res.data),

  checkPickupLocation: async (latitude: number, longitude: number, address?: string): Promise<ApiResponse<{
    isWithinCoverage: boolean;
    message?: string;
    nearestCity?: string;
    distanceToNearest?: number;
  }>> => {
    try {
      const response = await api.get('/api/bookings/check-pickup-location', { 
        params: { 
          latitude, 
          longitude, 
          ...(address && { address }),
          source: 'web' 
        } 
      });
      // If API returns success: false, throw an error so it's caught by the catch block
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to check pickup location');
      }
      return response.data;
    } catch (error: any) {
      // Re-throw with proper error message
      if (error.response?.data) {
        throw {
          response: {
            data: {
              message: error.response.data.message || 'Failed to check pickup location',
              success: false
            }
          }
        };
      }
      throw error;
    }
  },
};

// Driver Earnings API
export const driverEarningsAPI = {
  getDriverEarnings: (): Promise<ApiResponse<{
    overview: {
      totalEarnings: number;
      totalBookings: number;
      avgEarningsPerBooking: number;
      thisWeekEarnings: number;
      thisWeekBookings: number;
      lastWeekEarnings: number;
      lastWeekBookings: number;
      thisYearEarnings: number;
    };
    weeklyEarnings: Array<{
      week: string;
      earnings: number;
      bookings: number;
    }>;
    recentBookings: Array<{
      bookingId: number;
      date: string;
      amount: number;
      createdAt: string;
    }>;
  }>> =>
    api.get('/api/driver/earnings').then(res => res.data),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: (): Promise<ApiResponse<any[]>> =>
    api.get('/api/notifications').then(res => res.data),
  
  getUnreadCount: (): Promise<ApiResponse<{ count: number }>> =>
    api.get('/api/notifications/unread-count').then(res => res.data),
  
  markAsRead: (notificationId: string): Promise<ApiResponse<any>> =>
    api.patch(`/api/notifications/${notificationId}/read`).then(res => res.data),
  
  markAllAsRead: (): Promise<ApiResponse<any>> =>
    api.post('/api/notifications/mark-all-read').then(res => res.data),
  
  clearAll: (): Promise<ApiResponse<{ count: number }>> =>
    api.post('/api/notifications/clear-all').then(res => res.data),
  
  dismiss: (notificationId: string): Promise<ApiResponse<any>> =>
    api.patch(`/api/notifications/${notificationId}/dismiss`).then(res => res.data),
};

// Payments API
export const paymentsAPI = {
  createPaymentIntent: (paymentData: {
    bookingId: string;
    paymentMethod?: string;
    currency?: string;
  }): Promise<ApiResponse<{
    transactionId: string;
    gatewayTransactionId: string;
    amount: number;
    currency: string;
    paymentGateway: string;
  }>> => 
    api.post('/api/payments/create-intent', paymentData).then(res => res.data),

  verifyPayment: (verificationData: {
    transactionId: string;
    gatewayTransactionId: string;
    paymentGateway: 'razorpay' | 'stripe' | 'cashfree';
  }): Promise<ApiResponse<{
    transactionId: string;
    gatewayTransactionId: string;
    status: string;
  }>> => 
    api.post('/api/payments/verify', verificationData).then(res => res.data),

  getPaymentStatus: (transactionId: string): Promise<ApiResponse<{ payment: Payment }>> => 
    api.get(`/api/payments/status/${transactionId}`).then(res => res.data),

  getPaymentHistory: (params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ payments: Payment[]; pagination: any }>> => 
    api.get('/api/payments/history', { params }).then(res => res.data),

  processRefund: (transactionId: string, refundData: {
    amount: number;
    reason: string;
  }): Promise<ApiResponse<{ payment: Payment }>> => 
    api.post(`/api/payments/refund/${transactionId}`, refundData).then(res => res.data),

  getPaymentAnalytics: (): Promise<ApiResponse<any>> => 
    api.get('/api/payments/analytics').then(res => res.data),
};

// Categories API
export const categoriesAPI = {
  getCategories: (): Promise<ApiResponse<{ categories: Category[] }>> => 
    api.get('/api/categories').then(res => res.data),

  getCategoryById: (id: string): Promise<ApiResponse<{ category: Category }>> => 
    api.get(`/api/categories/${id}`).then(res => res.data),

  createCategory: (categoryData: {
    name: string;
    slug: string;
    description: string;
  }): Promise<ApiResponse<{ category: Category }>> => 
    api.post('/api/categories', categoryData).then(res => res.data),

  updateCategory: (id: string, categoryData: Partial<Category>): Promise<ApiResponse<{ category: Category }>> => 
    api.put(`/api/categories/${id}`, categoryData).then(res => res.data),

  deleteCategory: (id: string): Promise<ApiResponse<null>> => 
    api.delete(`/api/categories/${id}`).then(res => res.data),
};

// Admin API
export const adminAPI = {
  // Admin booking management
  getAllBookings: (params?: { 
    status?: string; 
    providerId?: string; 
    customerId?: string; 
    page?: number; 
    limit?: number; 
  }): Promise<ApiResponse<{ bookings: Booking[]; pagination: any }>> =>
    api.get('/api/admin/bookings', { params }).then(res => res.data),
  
  getBookingAnalytics: (): Promise<ApiResponse<{
    overview: {
      totalBookings: number;
      confirmedBookings: number;
      completedBookings: number;
      cancelledBookings: number;
      pendingBookings: number;
      totalRevenue: number;
      pendingRevenue: number;
    };
    charts: {
      statusDistribution: Array<{ _id: string; count: number }>;
      dailyBookings: Array<{ _id: string; count: number; revenue: number }>;
      topProviders: Array<{ name: string; email: string; bookingCount: number; revenue: number }>;
    };
  }>> =>
    api.get('/api/admin/bookings/analytics').then(res => res.data),
  
  // Admin schedule management
  getAllSchedules: (params?: { 
    providerId?: string; 
    date?: string; 
    month?: string; 
    year?: string; 
  }): Promise<ApiResponse<{ schedules: Schedule[] }>> =>
    api.get('/api/admin/schedules', { params }).then(res => res.data),
  
  createProviderSchedule: (scheduleData: { 
    providerId: string; 
    date: string; 
    timeSlots: { startTime: string; endTime: string; isAvailable?: boolean }[] 
  }): Promise<ApiResponse<{ schedule: Schedule }>> =>
    api.post('/api/admin/schedules', scheduleData).then(res => res.data),

  // Admin booking management
  acceptBooking: (bookingId: string): Promise<ApiResponse<{ booking: Booking }>> =>
    api.patch(`/api/admin/bookings/${bookingId}/accept`).then(res => res.data),

  cancelBooking: (bookingId: string, reason?: string): Promise<ApiResponse<{ booking: Booking }>> =>
    api.patch(`/api/admin/bookings/${bookingId}/cancel`, { reason }).then(res => res.data),

  completeBooking: (bookingId: string): Promise<ApiResponse<{ booking: Booking }>> =>
    api.patch(`/api/admin/bookings/${bookingId}/complete`).then(res => res.data),
  
  // Marketing notifications
  sendMarketingNotification: (data: {
    title: string;
    message: string;
    targetAudience: 'customers' | 'drivers' | 'admins' | 'all' | 'anonymous_android' | 'anonymous_ios' | 'anonymous_all';
  }): Promise<ApiResponse<{ successCount: number; failCount: number; totalUsers?: number; totalDevices?: number }>> =>
    api.post('/api/admin/marketing-notification', data).then(res => res.data),
};

// Push token API
export const pushTokenAPI = {
  registerFCMToken: (fcmToken: string): Promise<ApiResponse<any>> =>
    api.post('/api/push-token/fcm', { fcmToken }).then(res => res.data),
  
  registerWebPushToken: (webPushToken: string): Promise<ApiResponse<any>> =>
    api.post('/api/push-token/web', { webPushToken }).then(res => res.data),
  
  removePushToken: (tokenType?: 'fcm' | 'web'): Promise<ApiResponse<any>> =>
    api.delete('/api/push-token', { data: { tokenType } }).then(res => res.data),
};

// Admin Settings API
export const adminSettingsAPI = {
  getSettings: (): Promise<ApiResponse<AdminSettings>> =>
    api.get('/api/admin-settings').then(res => res.data),

  updateSettings: (settings: Partial<AdminSettings>): Promise<ApiResponse<AdminSettings>> =>
    api.put('/api/admin-settings', settings).then(res => res.data),

  updateDistanceRanges: (ranges: {
    firstRange?: number;
    secondRange?: number;
    thirdRange?: number;
    showAllRange?: number;
  }): Promise<ApiResponse<any>> =>
    api.put('/api/admin-settings/distance-ranges', ranges).then(res => res.data),

  updateDriverPricing: (pricing: {
    roundTripSlabs?: Array<any>;
    oneWaySlabs?: Array<any>;
    platformFeePercentage?: number;
    taxPercentage?: number;
  }): Promise<ApiResponse<any>> =>
    api.put('/api/admin-settings/driver-pricing', pricing).then(res => res.data),

  updateDriverIncentiveRanges: (data: {
    driverIncentiveRanges: Array<{
      minRides: number;
      maxRides: number | null;
      amount: number;
    }>;
  }): Promise<ApiResponse<any>> =>
    api.put('/api/admin-settings/driver-incentive-ranges', data).then(res => res.data),

  updateIncentiveCheckpoints: (checkpoints: Array<{
    bookings: number;
    amount: number;
  }>): Promise<ApiResponse<any>> =>
    api.put('/api/admin-settings/incentive-checkpoints', { incentiveCheckpoints: checkpoints }).then(res => res.data),

  calculateDriverPrice: (data: {
    distance: number;
    numberOfNights: number;
    tripType?: 'roundTrip' | 'oneWay';
    tripStartTime?: string;
    tripEndTime?: string;
  }): Promise<ApiResponse<{
    distanceCost: number;
    nightlyCost: number;
    subtotal: number;
    platformFee: number;
    tax: number;
    total: number;
    overtimeCharge?: number;
    nightDriveCharge?: number;
    incentiveEligible?: boolean;
    incentiveAmount?: number;
    incentiveTier?: number;
    slabUsed?: string;
    distance?: number;
  }>> =>
    api.post('/api/admin-settings/calculate-driver-price', data).then(res => res.data),

  calculateMultiDestinationPrice: (data: {
    origin: { latitude: number; longitude: number };
    intermediateDestinations: Array<{ latitude: number; longitude: number }>;
    finalDestination: { latitude: number; longitude: number };
    numberOfNights: number;
    isTwoWay: boolean;
  }): Promise<ApiResponse<{
    distanceCost: number;
    nightlyCost: number;
    subtotal: number;
    platformFee: number;
    tax: number;
    total: number;
    distance: number;
    slabUsed?: string;
  }>> =>
    api.post('/api/admin-settings/calculate-multi-destination-price', data).then(res => res.data),

  // MessageCentral OTP Feature Management
  getMessageCentralCostEstimates: (): Promise<ApiResponse<{
    current: {
      total: { total: number; currency: string; formatted: string };
      thisMonth: { total: number; currency: string; formatted: string };
    };
    projected: {
      thisMonth: { total: number; currency: string; formatted: string };
      thisYear: { total: number; currency: string; formatted: string };
    };
    usage: {
      total: number;
      thisMonth: number;
      dailyAverage: number;
    };
    settings: {
      costPerOTP: number;
      currency: string;
      exchangeRate: number;
    };
  }>> =>
    api.get('/api/admin-settings/messagecentral/cost-estimates').then(res => res.data),

  toggleMessageCentralOTP: (enabled: boolean): Promise<ApiResponse<{
    enabled: boolean;
    costEstimates: any;
  }>> =>
    api.put('/api/admin-settings/messagecentral/toggle', { enabled }).then(res => res.data),

  updateMessageCentralOTPSettings: (settings: {
    costPerOTP?: number;
    currency?: string;
    exchangeRate?: number;
  }): Promise<ApiResponse<{
    settings: any;
    costEstimates: any;
  }>> =>
    api.put('/api/admin-settings/messagecentral/settings', settings).then(res => res.data),

  // Unified Feature Flags Management
  getAllFeatureFlags: (params?: { year?: number; month?: number; previousMonth?: boolean }): Promise<ApiResponse<{
    features: Array<{
      name: string;
      enabled: boolean;
      usage: {
        total: number;
        thisMonth: number;
        dailyAverage: number;
      };
      costs: {
        total: { total: number; currency: string; formatted: string };
        thisMonth: { total: number; currency: string; formatted: string };
        projectedMonthly: { total: number; currency: string; formatted: string };
      };
      settings: {
        costPerUnit?: number;
        costPer1000Units?: number;
        freeTierLimit?: number;
        currency: string;
        exchangeRate: number;
      };
    }>;
    aggregate: {
      totalThisMonth: { total: number; currency: string; formatted: string };
      totalAllTime: { total: number; currency: string; formatted: string };
      projectedMonthly: { total: number; currency: string; formatted: string };
      features: number;
    };
  }>> =>
    api.get('/api/admin-settings/feature-flags', { params }).then(res => res.data),

  toggleFeatureFlag: (featureName: string, enabled: boolean): Promise<ApiResponse<{
    featureName: string;
    enabled: boolean;
    features: any[];
    aggregate: any;
  }>> =>
    api.put('/api/admin-settings/feature-flags/toggle', { featureName, enabled }).then(res => res.data),

  updateFeatureFlagPricing: (featureName: string, pricing: {
    costPerOTP?: number;
    costPer1000Requests?: number;
    costPerNotification?: number;
    costPer1000Emails?: number;
    costPer1000Events?: number;
    freeTierLimit?: number;
    currency?: string;
    exchangeRate?: number;
  }): Promise<ApiResponse<{
    featureName: string;
    features: any[];
    aggregate: any;
  }>> =>
    api.put('/api/admin-settings/feature-flags/pricing', { featureName, pricing }).then(res => res.data),

  // Platform Costs Management
  getPlatformCosts: (params?: { year?: number; month?: number }): Promise<ApiResponse<{
    costs: Array<{
      id: string;
      name: string;
      category: string;
      provider: string;
      costType: string;
      amount: number;
      currency: string;
      monthlyCost: { total: number; currency: string; formatted: string };
      yearlyCost: { total: number; currency: string; formatted: string };
      description?: string;
      notes?: string;
      yearlyPaymentMonth?: number;
    }>;
    totals: {
      monthly: { total: number; currency: string; formatted: string };
      yearly: { total: number; currency: string; formatted: string };
    };
    byCategory: Record<string, any[]>;
  }>> =>
    api.get('/api/admin-settings/platform-costs', { params }).then(res => res.data),

  getMonthlyCostBreakdown: (year: number): Promise<ApiResponse<{
    year: number;
    monthlyData: Array<{
      month: string;
      monthIndex: number;
      total: number;
      formatted: string;
      platformCost: { total: number; formatted: string; breakdown: Array<{ name: string; amount: number; type: string }> };
      featureFlagCost: { total: number; formatted: string; breakdown: Array<{ name: string; amount: number; type: string }> };
      breakdown: Array<{ name: string; amount: number; type: string }>;
    }>;
    yearlyTotal: { total: number; currency: string; formatted: string };
    yearlyPlatformTotal: { total: number; currency: string; formatted: string };
    yearlyFeatureFlagTotal: { total: number; currency: string; formatted: string };
  }>> =>
    api.get('/api/admin-settings/platform-costs/monthly-breakdown', { params: { year } }).then(res => res.data),

  upsertPlatformCost: (data: {
    name: string;
    category: string;
    provider: string;
    costType: string;
    amount: number;
    currency?: string;
    exchangeRate?: number;
    billingCycle?: string;
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
    description?: string;
    notes?: string;
    yearlyPaymentMonth?: number;
  }): Promise<ApiResponse<{
    cost: any;
    summary: any;
    aggregate: any;
  }>> =>
    api.post('/api/admin-settings/platform-costs', data).then(res => res.data),

  updatePlatformCost: (id: string, data: {
    name?: string;
    category?: string;
    provider?: string;
    costType?: string;
    amount?: number;
    currency?: string;
    exchangeRate?: number;
    billingCycle?: string;
    yearlyPaymentMonth?: number;
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
    description?: string;
    notes?: string;
  }): Promise<ApiResponse<{
    cost: any;
    summary: any;
    aggregate: any;
  }>> =>
    api.put(`/api/admin-settings/platform-costs/${id}`, data).then(res => res.data),

  deletePlatformCost: (id: string): Promise<ApiResponse<{
    summary: any;
    aggregate: any;
  }>> =>
    api.delete(`/api/admin-settings/platform-costs/${id}`).then(res => res.data),

  // City and Coverage Management
  getCitiesAndCoverage: (): Promise<ApiResponse<{
    cities: string[];
    coverageAreas: Array<{
      cityName: string;
      centerPoint: { latitude: number; longitude: number };
      radiusKm: number;
    }>;
  }>> =>
    api.get('/api/admin-settings/cities-coverage').then(res => res.data),

  getExpansionRecommendations: (): Promise<ApiResponse<{
    recommendations: Array<{
      centerLat: number;
      centerLng: number;
      requestCount: number;
      estimatedMonthlyBookings: number;
      nearestCity?: string;
      avgDistanceToNearest?: number;
      sampleAddress?: string;
      lastRequestDate: Date;
    }>;
    totalRejectedRequests: number;
    period: string;
    clusterRadiusKm: number;
  }>> =>
    api.get('/api/admin-settings/expansion-recommendations').then(res => res.data),

  getRejectedLocationsRaw: (): Promise<ApiResponse<{
    locations: Array<{
      latitude: number;
      longitude: number;
      address?: string;
      requestedAt: Date;
      source?: 'ios' | 'android' | 'web';
      nearestCity?: string;
      distanceToNearest?: number;
    }>;
    totalCount: number;
    period: string;
  }>> =>
    api.get('/api/admin-settings/rejected-locations/raw').then(res => res.data),

  getRejectedLocationsBubbleView: (bubbleRadiusKm?: number): Promise<ApiResponse<{
    bubbles: Array<{
      cityName: string;
      centerLat: number;
      centerLng: number;
      requestCount: number;
      radiusKm: number;
    }>;
    totalRejectedRequests: number;
    bubbleRadiusKm: number;
    period: string;
  }>> =>
    api.get('/api/admin-settings/rejected-locations/bubbles', { 
      params: bubbleRadiusKm ? { bubbleRadiusKm } : {} 
    }).then(res => res.data),

  getCachedMap: (viewType: 'coverage' | 'rejected', zoomLevel: 'city' | 'state', viewMode?: 'dots' | 'bubbles', forceRefresh?: boolean): Promise<ApiResponse<{
    cached: boolean;
    mapData?: any;
    lastUpdated?: Date;
    expiresAt?: Date;
    shouldFetch?: boolean;
  }>> =>
    api.get('/api/admin-settings/map-cache', { 
      params: { viewType, zoomLevel, ...(viewMode && { viewMode }), ...(forceRefresh && { forceRefresh: 'true' }) } 
    }).then(res => res.data),

  saveMapCache: (viewType: 'coverage' | 'rejected', zoomLevel: 'city' | 'state', mapData: any, viewMode?: 'dots' | 'bubbles'): Promise<ApiResponse<{
    message: string;
  }>> =>
    api.post('/api/admin-settings/map-cache', { viewType, zoomLevel, mapData, ...(viewMode && { viewMode }) }).then(res => res.data),

  updateCities: (cities: string[]): Promise<ApiResponse<string[]>> =>
    api.put('/api/admin-settings/cities', { cities }).then(res => res.data),

  updateCoverageAreas: (coverageAreas: Array<{
    cityName: string;
    centerPoint: { latitude: number; longitude: number };
    radiusKm: number;
  }>): Promise<ApiResponse<Array<{
    cityName: string;
    centerPoint: { latitude: number; longitude: number };
    radiusKm: number;
  }>>> =>
    api.put('/api/admin-settings/coverage-areas', { coverageAreas }).then(res => res.data),
};

// User Management API (Admin only)
export const userManagementAPI = {
  getAllUsers: (params?: {
    role?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    users: User[];
    stats: {
      totalUsers: number;
      customers: number;
      providers: number;
      admins: number;
    };
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  }>> =>
    api.get('/api/admin/users', { params }).then(res => res.data),

  getUserById: (id: string): Promise<ApiResponse<{ user: User }>> =>
    api.get(`/api/admin/users/${id}`).then(res => res.data),

  changeUserRole: (id: string, role: 'customer' | 'provider' | 'admin', skills?: string[]): Promise<ApiResponse<{ user: User }>> =>
    api.patch(`/api/admin/users/${id}/role`, { role, skills }).then(res => res.data),

  updateUser: (id: string, userData: Partial<User>): Promise<ApiResponse<{ user: User }>> =>
    api.put(`/api/admin/users/${id}`, userData).then(res => res.data),

  updateUserPassword: (id: string, password: string): Promise<ApiResponse<null>> =>
    api.patch(`/api/admin/users/${id}/password`, { password }).then(res => res.data),

  deleteUser: (id: string): Promise<ApiResponse<null>> =>
    api.delete(`/api/admin/users/${id}`).then(res => res.data),

  toggleBlacklist: (id: string, isBlacklisted: boolean): Promise<ApiResponse<{ user: User }>> =>
    api.patch(`/api/admin/users/${id}/blacklist`, { isBlacklisted }).then(res => res.data),

  getDeletionRequests: (): Promise<ApiResponse<{
    requests: Array<User & {
      accountDeletionRequestedAt?: string;
      accountDeletionReason?: string;
    }>;
    count: number;
  }>> => api.get('/api/admin/users/deletion-requests').then(res => res.data),

  approveAccountDeletion: (id: string): Promise<ApiResponse<null>> =>
    api.post(`/api/admin/users/${id}/approve-deletion`).then(res => res.data),

  rejectAccountDeletion: (id: string, rejectionReason?: string): Promise<ApiResponse<{ user: User }>> =>
    api.post(`/api/admin/users/${id}/reject-deletion`, { rejectionReason }).then(res => res.data),
};

// Location API (browser geolocation wrapper)
export const locationAPI = {
  getCurrentLocation: (): Promise<{ latitude: number; longitude: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  },
};

// KYC API
export const kycAPI = {
  submitKyc: (documents: Array<{ type: string; url: string }>): Promise<ApiResponse<{ kycStatus: string }>> =>
    api.post('/api/kyc/submit', { documents }).then(res => res.data),

  uploadDocument: (formData: FormData): Promise<ApiResponse<{ url: string; filename: string; documentType: string }>> =>
    api.post('/api/kyc/upload-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data),

  getMyKycStatus: (): Promise<ApiResponse<{ 
    kycStatus: string; 
    kycDocuments?: Array<{ type: string; url: string; uploadedAt: Date }>; 
    kycRejectionReason?: string 
  }>> => 
    api.get('/api/kyc/status').then(res => res.data),

  getAllKycSubmissions: (status?: string): Promise<ApiResponse<{ submissions: any[] }>> =>
    api.get('/api/kyc/submissions', { params: { status } }).then(res => res.data),

  reviewKyc: (userId: string, status: 'approved' | 'rejected', rejectionReason?: string): Promise<ApiResponse<any>> =>
    api.post(`/api/kyc/review/${userId}`, { status, rejectionReason }).then(res => res.data),
};

// Police Verification API
export const policeVerificationAPI = {
  getAllSubmissions: (status?: string): Promise<ApiResponse<{ submissions: any[] }>> =>
    api.get('/api/police-verification/submissions', { params: { status } }).then(res => res.data),

  reviewPoliceVerification: (userId: string, status: 'approved' | 'rejected', rejectionReason?: string): Promise<ApiResponse<any>> =>
    api.post(`/api/police-verification/review/${userId}`, { status, rejectionReason }).then(res => res.data),
};

// Legal Documents API
export const legalAPI = {
  getLegalDocument: (type: 'privacy' | 'terms', userType: 'driver' | 'customer', language: string = 'en'): Promise<ApiResponse<{
    _id: string;
    type: string;
    userType: string;
    content: string;
    version: string;
    isActive: boolean;
    language?: string;
  }>> =>
    api.get(`/api/legal/${type}/${userType}`, { params: { lang: language } }).then(res => res.data),
};

// Driver Incentive API
export const driverIncentiveAPI = {
  getMyIncentives: (): Promise<ApiResponse<{
    incentives: Array<{
      _id: string;
      checkpoint: number;
      amount: number;
      status: 'pending' | 'awarded' | 'paid';
      awardedAt: string;
      bookingId?: any;
    }>;
    totalPending: number;
    totalPaid: number;
  }>> =>
    api.get('/api/driver-incentives/my-incentives').then(res => res.data),
};

export default api;
